// src/pages/AnnouncementsPage.js
import { useState, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { getAnnouncements, postAnnouncement } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY = '#1B3A6B';
const GOLD = '#C9A84C';
const RED  = '#c0392b';

export default function AnnouncementsPage() {
  const { role }        = useAuth();

  const isExecutive     = role === 'executive';

  const [announcements, setAnnouncements] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [selected,      setSelected]      = useState(null);

  // New announcement form
  const [form,    setForm]    = useState({
    title: '', body: '', priority: 'normal'
  });
  const [posting, setPosting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadAnnouncements();
  }, []);

  async function loadAnnouncements() {
    setLoading(true);
    try {
      const result = await getAnnouncements();
      setAnnouncements(result.announcements || []);
    } catch (err) {
      setError('Could not load announcements: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const handlePost = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and message are required.');
      return;
    }
    setPosting(true);
    setError('');
    try {
      await postAnnouncement(form);
      setSuccess('Announcement posted successfully.');
      setForm({ title: '', body: '', priority: 'normal' });
      setShowForm(false);
      await loadAnnouncements();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to post: ' + err.message);
    } finally {
      setPosting(false);
    }
  };


  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric',
        month: 'long', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '800px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              📢 Announcements
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
              {loading ? 'Loading...'
                : `${announcements.length} announcement${announcements.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isExecutive && (
            <button
              onClick={() => {
                setShowForm(!showForm);
                setError('');
              }}
              style={{
                background: GOLD, color: NAVY,
                border: 'none', borderRadius: '8px',
                padding: '10px 20px', cursor: 'pointer',
                fontWeight: 'bold', fontSize: '14px'
              }}
            >
              {showForm ? '✕ Cancel' : '+ New Announcement'}
            </button>
          )}
        </div>

        {/* Success */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: '#1a6b3a', fontSize: '14px'
          }}>
            ✓ {success}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* New Announcement Form */}
        {showForm && isExecutive && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px', marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `2px solid ${GOLD}`
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              New Announcement
            </h3>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>
              {/* Title */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e =>
                    setForm(p => ({ ...p, title: e.target.value }))
                  }
                  placeholder="Announcement title"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Body */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Message *
                </label>
                <textarea
                  value={form.body}
                  onChange={e =>
                    setForm(p => ({ ...p, body: e.target.value }))
                  }
                  placeholder="Write your announcement here..."
                  rows={5}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0', borderRadius: '8px',
                    fontSize: '14px', outline: 'none',
                    boxSizing: 'border-box', resize: 'vertical',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              {/* Priority */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555', marginBottom: '6px'
                }}>
                  Priority
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {[
                    { value: 'normal', label: '📋 Normal' },
                    { value: 'urgent', label: '🚨 Urgent' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        setForm(p => ({ ...p, priority: opt.value }))
                      }
                      style={{
                        padding: '8px 20px',
                        background: form.priority === opt.value
                          ? NAVY : '#f0f4f8',
                        color: form.priority === opt.value
                          ? '#fff' : '#555',
                        border: `2px solid ${form.priority === opt.value
                          ? NAVY : '#e0e0e0'}`,
                        borderRadius: '8px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '600'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Post button */}
              <button
                onClick={handlePost}
                disabled={posting}
                style={{
                  padding: '12px 24px',
                  background: NAVY, color: '#fff',
                  border: 'none', borderRadius: '8px',
                  cursor: posting ? 'not-allowed' : 'pointer',
                  fontSize: '15px', fontWeight: 'bold',
                  opacity: posting ? 0.7 : 1,
                  alignSelf: 'flex-end'
                }}
              >
                {posting ? 'Posting...' : 'Post Announcement'}
              </button>
            </div>
          </div>
        )}

        {/* Announcements List */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading announcements...
          </div>
        ) : announcements.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '60px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              📢
            </div>
            <h3 style={{ color: NAVY, margin: '0 0 8px' }}>
              No announcements yet
            </h3>
            <p style={{ color: '#666', margin: 0 }}>
              {isExecutive
                ? 'Post your first announcement using the button above.'
                : 'Check back later for updates from the executives.'}
            </p>
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '16px'
          }}>
            {announcements.map(ann => (
              <div
                key={ann.id}
                onClick={() =>
                  setSelected(selected?.id === ann.id ? null : ann)
                }
                style={{
                  background: '#fff', borderRadius: '12px',
                  padding: '20px', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: selected?.id === ann.id
                    ? `2px solid ${NAVY}`
                    : ann.priority === 'urgent'
                    ? `2px solid ${RED}`
                    : '2px solid transparent',
                  transition: 'border-color 0.2s'
                }}
              >
                {/* Announcement Header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', gap: '12px',
                  marginBottom: selected?.id === ann.id ? '16px' : '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: '8px', marginBottom: '4px',
                      flexWrap: 'wrap'
                    }}>
                      {ann.priority === 'urgent' && (
                        <span style={{
                          background: '#fdecea', color: RED,
                          fontSize: '11px', fontWeight: 'bold',
                          padding: '2px 8px', borderRadius: '4px'
                        }}>
                          🚨 URGENT
                        </span>
                      )}
                      <h3 style={{
                        margin: 0, fontSize: '16px',
                        color: NAVY, fontWeight: 'bold'
                      }}>
                        {ann.title}
                      </h3>
                    </div>
                    <div style={{
                      fontSize: '12px', color: '#888'
                    }}>
                      {formatDate(ann.date)}
                    </div>
                  </div>
                  <div style={{
                    color: '#aaa', fontSize: '18px',
                    flexShrink: 0
                  }}>
                    {selected?.id === ann.id ? '▲' : '▼'}
                  </div>
                </div>

                {/* Preview text when collapsed */}
                {selected?.id !== ann.id && (
                  <p style={{
                    margin: 0, fontSize: '14px', color: '#666',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {ann.body}
                  </p>
                )}

                {/* Full text when expanded */}
                {selected?.id === ann.id && (
                  <div>
                    <div style={{
                      fontSize: '15px', color: '#333',
                      lineHeight: '1.6', whiteSpace: 'pre-wrap',
                      marginBottom: '16px'
                    }}>
                      {ann.body}
                    </div>
                    <div style={{
                      fontSize: '12px', color: '#aaa',
                      paddingTop: '12px',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      Posted by {ann.postedBy}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}